import { UI_ONLY_NODE_TYPES } from "../constants";
import { AFTER_LAST_SENTINEL_ID, CONTINUE_SENTINEL_ID, FOREACH_SENTINEL_ID } from "../nodes/loop/loopTypes";
import type { RuleEdge } from "../types/edges";
import type { LoopNodeData, RuleNode } from "../types/nodes";

/**
 * All rule node ids under a subtree (including nested loop bodies).
 */
const collectNestedRuleNodeIds = (nodes: RuleNode[]): Set<string> => {
	const ids = new Set<string>();
	const visit = (list: RuleNode[]) => {
		for (const n of list) {
			ids.add(n.id);
			if (n.type === "loop") {
				visit((n.data as LoopNodeData).loopNodes ?? []);
			}
		}
	};
	visit(nodes);
	return ids;
};

const isValidLoopEdgeEndpoint = (id: string, bodyIds: Set<string>): boolean =>
	id === FOREACH_SENTINEL_ID || id === AFTER_LAST_SENTINEL_ID || id === CONTINUE_SENTINEL_ID || bodyIds.has(id);

/**
 * Keeps only edges whose source and target refer to sentinel ids or a node in this loop body (recursively).
 */
export const pruneLoopEdgesForBody = (loopNodes: RuleNode[], loopEdges: RuleEdge[]): RuleEdge[] => {
	const bodyIds = collectNestedRuleNodeIds(loopNodes);
	return loopEdges.filter(
		(e) => isValidLoopEdgeEndpoint(e.source, bodyIds) && isValidLoopEdgeEndpoint(e.target, bodyIds),
	);
};

const sanitizeLoopNodeDataDeep = (data: LoopNodeData): LoopNodeData => {
	const innerNodes = (data.loopNodes ?? [])
		.filter((child) => !UI_ONLY_NODE_TYPES.has(child.type ?? ""))
		.map((child) => {
			if (child.type !== "loop") return child;
			return {
				...child,
				data: sanitizeLoopNodeDataDeep(child.data as LoopNodeData),
			};
		});
	return {
		...data,
		loopNodes: innerNodes,
		loopEdges: pruneLoopEdgesForBody(innerNodes, data.loopEdges ?? []),
	};
};

/**
 * Drops stale edges after deletes missed in UI (e.g. keyboard remove) and prunes nested `loopEdges`
 * so save payloads never reference removed node ids.
 */
export const sanitizeRuleGraphForSave = (
	nodes: RuleNode[],
	edges: RuleEdge[],
): { nodes: RuleNode[]; edges: RuleEdge[] } => {
	const topLevelIds = new Set(nodes.map((n) => n.id));
	const prunedMainEdges = edges.filter((e) => topLevelIds.has(e.source) && topLevelIds.has(e.target));

	const sanitizedNodes = nodes.map((n) => {
		if (n.type !== "loop") return n;
		return {
			...n,
			data: sanitizeLoopNodeDataDeep(n.data as LoopNodeData),
		};
	});

	return { nodes: sanitizedNodes, edges: prunedMainEdges };
};
